import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  getTemplate, getPage, getGlobalSection, listVersions, saveDraft, publishVersion,
  listGlobalSections, globalSectionUsage,
  type SectionUsage, type CmsPage, type CmsTemplate, type CmsVersion,
  type GlobalSection, type OwnerRef,
} from '../../lib/cmsAdmin';
import { searchEventsForOpportunity, type SelectableEvent } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useBlockMetadata } from '../../admin/cms/useBlockMetadata';
import { Outline } from '../../admin/cms/Outline';
import { Palette } from '../../admin/cms/Palette';
import { Inspector } from '../../admin/cms/Inspector';
import { EditorPreview } from '../../admin/cms/EditorPreview';
import { PublishDialog } from '../../admin/cms/PublishDialog';
import { ThemeControl } from '../../admin/cms/ThemeControl';
import { VersionHistory } from '../../admin/cms/VersionHistory';
import {
  editorReducer, initialEditorState, isDirty,
} from '../../cms/editor/reducer';
import {
  findNode, validateForEditor, nodeCount, EDITOR_LIMITS,
  type BlockDocument, type EditorContext,
} from '../../cms/editor/document';
import type { RenderProblem } from '../../cms/BlockRenderer';
import './AdminCms.css';

/**
 * The CMS editor.
 *
 * ── WHAT IT IS, AND WHAT IT DELIBERATELY IS NOT ──────────────────────────────────────
 *
 * It edits COMPOSITION: which blocks a page is made of, in what order, with what
 * settings. It is not a visual design tool. There is no free-text class, no style panel,
 * no HTML field and no script field, because each of those turns a document into a place
 * where code reaches the page — which is the one thing this CMS is built not to allow.
 *
 * ── EVERY EDIT GOES THROUGH THE PURE UTILITIES ───────────────────────────────────────
 *
 * The reducer applies operations from cms/editor/document.ts, which are unit-tested
 * without a browser and refuse rather than repair. So this component never manipulates a
 * document directly: it dispatches, renders the result, and shows the reason when an
 * operation was refused.
 *
 * ── THREE PLACES SAY NO, AND THEY ARE NOT THE SAME NO ────────────────────────────────
 *
 *   the utilities   refuse an operation that would corrupt the document (here, instantly)
 *   validateDocument  refuses a structurally invalid save (server, every save)
 *   the publish gate  refuses a document that must not go public (server, publish only)
 *
 * The editor mirrors the last two so an admin is not surprised, but the server's answer
 * is the answer. Nothing below is a substitute for it.
 */
export function CmsEditor({ kind }: { kind: 'template' | 'page' | 'global_section' }) {
  const params = useParams<{ templateId?: string; pageId?: string; sectionId?: string }>();
  const id = kind === 'template' ? params.templateId
    : kind === 'page' ? params.pageId : params.sectionId;
  const [searchParams, setSearchParams] = useSearchParams();

  const owner = useMemo<OwnerRef | null>(() => (id ? { kind, id } : null), [kind, id]);

  const [record, setRecord] = useState<CmsTemplate | CmsPage | GlobalSection | null>(null);
  // §64: the outline shows what a reference points AT, so the names are loaded once.
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [state, dispatch] = useReducer(editorReducer, null, () => initialEditorState(
    EMPTY_DOCUMENT, 'event', null));
  const [ready, setReady] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [renderProblems, setRenderProblems] = useState<RenderProblem[]>([]);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);

  const { manifests, loading: blocksLoading, error: blocksError } = useBlockMetadata();

  // Which event the centre preview renders against. In the URL, so a preview somebody is
  // looking at can be linked to and reloaded — the same rule as admin scope.
  const previewEventId = searchParams.get('preview');
  const [events, setEvents] = useState<SelectableEvent[]>([]);


  // ── Load ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = owner.kind === 'template' ? await getTemplate(owner.id)
        : owner.kind === 'page' ? await getPage(owner.id)
          : await getGlobalSection(owner.id);
      setRecord(fetched);

      const versions = await listVersions(owner);
      const draft = versions.find((v) => v.state === 'draft') ?? null;
      const published = versions.find((v) => v.state === 'published') ?? null;
      setPublishedVersion(published?.version ?? null);

      // With no draft, editing starts from what is live — the usual intent is "change
      // the page", not "start from nothing". Saving then creates the draft.
      const startingPoint = draft?.document ?? published?.document ?? EMPTY_DOCUMENT;
      const context = owner.kind === 'global_section'
        ? 'global_section'
        : (('entityKind' in fetched ? fetched.entityKind : null) ?? 'standalone');

      // The context travels with the document. Setting it separately is how the palette
      // came to offer blocks the reducer refused: the editor knew it was editing a
      // global section and the reducer still thought it was an event page.
      dispatch({
        type: 'REPLACE_DOCUMENT',
        document: startingPoint as BlockDocument,
        savedVersion: draft?.version ?? null,
        context: context as EditorContext,
      });
      dispatch({ type: 'MARK_SAVED', document: startingPoint as BlockDocument, savedVersion: draft?.version ?? null });
      seoRef.current = draft?.seo ?? published?.seo ?? null;
      setReady(true);

      // An entity override previews against its own entity; nothing else to choose.
      if ('entityId' in fetched && fetched.entityId && !searchParams.get('preview')) {
        const next = new URLSearchParams(searchParams);
        next.set('preview', fetched.entityId);
        setSearchParams(next, { replace: true });
      }
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'This could not be loaded.');
    } finally {
      setLoading(false);
    }
    // searchParams deliberately omitted: re-running the load when the preview changes
    // would throw away unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  const seoRef = useRef<CmsVersion['seo']>(null);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    // Only for templates: an override already knows its event.
    if (kind !== 'template') return;
    searchEventsForOpportunity().then(setEvents).catch(() => setEvents([]));
  }, [kind]);

  useEffect(() => {
    listGlobalSections()
      .then((rows) => setSectionNames(
        Object.fromEntries(rows.map((r) => [r.id, r.name]))))
      .catch(() => setSectionNames({}));
  }, []);

  // ── Unsaved work ────────────────────────────────────────────────────────

  const dirty = isDirty(state);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // ── Validation, continuously ────────────────────────────────────────────

  const problems = useMemo(
    () => (ready ? validateForEditor(state.document, state.context) : []),
    [state.document, state.context, ready],
  );
  const problemNodeIds = useMemo(
    () => new Set(problems.map((p) => p.nodeId).filter((x): x is string => Boolean(x))),
    [problems],
  );
  const blocking = problems.filter((p) => p.severity === 'error');

  // ── Actions ─────────────────────────────────────────────────────────────

  async function save() {
    if (!owner) return;
    setSaving(true);
    setSaveError(null);
    try {
      const version = await saveDraft(owner, {
        document: state.document,
        // Preserved, never dropped: this editor does not edit SEO, and a save that
        // silently cleared it would lose work done elsewhere.
        seo: seoRef.current,
        expectedVersion: state.savedVersion,
      });
      dispatch({ type: 'MARK_SAVED', document: state.document, savedVersion: version.version });
      setFlash(`Draft saved as v${version.version}. Nothing is live until you publish.`);
    } catch (err) {
      setSaveError(conflictMessage(err) ?? (err instanceof ApiError
        ? err.message : 'The draft could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!owner) return;
    setSaving(true);
    try {
      const version = await publishVersion(owner, state.savedVersion);
      setShowPublish(false);
      setPublishedVersion(version.version);
      dispatch({ type: 'MARK_SAVED', document: state.document, savedVersion: null });
      setFlash(`Published v${version.version}. It is live now.`);
      await load();
    } catch (err) {
      setSaveError(conflictMessage(err) ?? (err instanceof ApiError
        ? err.message : 'The publish was refused.'));
      setShowPublish(false);
    } finally {
      setSaving(false);
    }
  }

  function restore(version: CmsVersion) {
    dispatch({ type: 'REPLACE_DOCUMENT', document: version.document as BlockDocument });
    setShowHistory(false);
    setFlash(`v${version.version} loaded into the editor. Save it to keep it, publish it to make it live.`);
  }

  const selected = state.selectedNodeId ? findNode(state.document, state.selectedNodeId) : null;
  const selectedManifest = selected
    ? manifests.find((m) => m.id === selected.component) ?? null : null;

  const name = record?.name ?? 'Loading…';
  const scope = record?.scope ?? 'organization';

  usePageChrome({
    breadcrumb: [
      { label: 'Content & design' },
      { label: LIST_LABEL[kind], to: LIST_PATH[kind] },
      { label: name },
    ],
  }, [name, kind]);

  if (loading || blocksLoading) return <p className="adm-lede">Loading the editor…</p>;
  if (loadError) return <div className="alert alert--error" role="alert">{loadError}</div>;
  if (blocksError) return <div className="alert alert--error" role="alert">{blocksError}</div>;
  if (!record || !owner) return null;

  return (
    <div className="cms-editor">
      <header className="cms-bar">
        <div className="cms-bar__id">
          <h1>{name}</h1>
          <p className="cms-bar__meta">
            <span className="badge">{scope === 'platform' ? 'Platform' : 'Organization'}</span>
            {'isDefault' in record && record.isDefault && <span className="badge badge--ok">Default</span>}
            {publishedVersion
              ? <span className="badge badge--ok">Live: v{publishedVersion}</span>
              : <span className="badge badge--pending">Never published</span>}
            {state.savedVersion && <span className="badge badge--pending">Draft v{state.savedVersion}</span>}
            {dirty && <span className="badge badge--error">Unsaved changes</span>}
          </p>
        </div>

        <div className="cms-bar__actions">
          <ThemeControl
            owner={owner}
            organizationId={record.organizationId}
            scope={scope}
            themeId={ownerThemeId(record)}
            onChanged={() => { void load(); }}
          />
          {kind === 'template' && (
            <label className="cms-bar__preview-pick">
              <span className="field__label">Preview with</span>
              <select
                className="adm-select"
                value={previewEventId ?? ''}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  if (e.target.value) next.set('preview', e.target.value);
                  else next.delete('preview');
                  setSearchParams(next, { replace: true });
                }}
              >
                <option value="">No event chosen</option>
                {events.map((ev) => (
                  <option key={ev.eventId} value={ev.eventId}>{ev.title}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="button button--ghost" onClick={() => setShowHistory(true)}>
            History
          </button>
          <button type="button" className="button button--secondary" disabled={!dirty || saving} onClick={() => { void save(); }}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={saving || blocking.length > 0 || dirty}
            title={dirty ? 'Save the draft before publishing it.'
              : blocking.length > 0 ? 'Fix the problems listed below first.' : undefined}
            onClick={() => setShowPublish(true)}
          >
            Publish
          </button>
        </div>
      </header>

      {/* §66. Editing a reusable section is not like editing a page: the change lands
          everywhere it is referenced, at once, the moment it is published. Somebody who
          arrived here from a template needs to be told that before they start, not
          after. The figure is the real one the publish dialog will use. */}
      {kind === 'global_section' && <SectionReach sectionId={owner.id} />}

      {flash && (
        <div className="adm-flash" role="status">
          {flash}
          <button type="button" className="cms-icon" onClick={() => setFlash(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {saveError && <div className="alert alert--error" role="alert">{saveError}</div>}
      {state.lastRefusal && (
        <div className="alert alert--warning" role="alert">
          {state.lastRefusal.message}
          <button type="button" className="cms-icon"
            onClick={() => dispatch({ type: 'DISMISS_REFUSAL' })} aria-label="Dismiss">×</button>
        </div>
      )}

      {problems.length > 0 && (
        <ul className="cms-problems">
          {problems.map((p, i) => (
            <li key={`${p.code}-${p.nodeId ?? i}`} className={`cms-problems__item is-${p.severity}`}>
              <button type="button"
                onClick={() => p.nodeId && dispatch({ type: 'SELECT_NODE', nodeId: p.nodeId })}>
                {p.message}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="cms-regions">
        <aside className="cms-region cms-region--left">
          <section>
            <h2 className="cms-region__title">Blocks on this page</h2>
            <Outline
              document={state.document}
              selectedNodeId={state.selectedNodeId}
              problemNodeIds={problemNodeIds}
              onSelect={(nodeId) => dispatch({ type: 'SELECT_NODE', nodeId })}
              onMove={(nodeId, direction) => dispatch({ type: 'MOVE_NODE', nodeId, direction })}
              onRemove={(nodeId) => dispatch({ type: 'REMOVE_NODE', nodeId })}
              onDuplicate={(nodeId) => dispatch({ type: 'DUPLICATE_NODE', nodeId })}
              sectionNames={sectionNames}
            />
            <p className="adm-muted cms-region__count">
              {nodeCount(state.document)} of {EDITOR_LIMITS.maxNodes} blocks
            </p>
          </section>

          <section>
            <h2 className="cms-region__title">Add a block</h2>
            <Palette
              document={state.document}
              manifests={manifests}
              selectedNodeId={state.selectedNodeId}
              context={state.context}
              onAdd={(component, parentId) => dispatch({ type: 'ADD_NODE', component, parentId })}
            />
          </section>
        </aside>

        <main className="cms-region cms-region--centre">
          <h2 className="cms-region__title">
            Preview
            {renderProblems.length > 0 && (
              <span className="badge badge--error">{renderProblems.length} not rendered</span>
            )}
            <span className="cms-widths" role="group" aria-label="Preview width">
              {(['desktop', 'mobile'] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  className={`cms-widths__button${previewWidth === w ? ' is-active' : ''}`}
                  aria-pressed={previewWidth === w}
                  onClick={() => setPreviewWidth(w)}
                >
                  {w === 'desktop' ? 'Desktop' : 'Mobile'}
                </button>
              ))}
            </span>
          </h2>
          <EditorPreview
            document={state.document}
            previewEventId={previewEventId}
            context={kind === 'global_section' ? 'global_section' : 'event'}
            width={previewWidth}
            onProblems={setRenderProblems}
          />
          <p className="adm-hint">
            {kind === 'global_section'
              ? 'Rendered by the same code a public page uses. A global section takes the '
                + 'theme of whatever page it appears on, so this preview shows Voleto\u2019s '
                + 'default rather than a theme of its own.'
              : 'Rendered by the same code the public page uses, with this event\u2019s real '
                + 'content. Prices, availability and the lineup come from the event, never '
                + 'from the layout.'}
          </p>
        </main>

        <aside className="cms-region cms-region--right">
          <h2 className="cms-region__title">Settings</h2>
          {selected
            ? (
              <Inspector
                node={selected}
                manifest={selectedManifest}
                organizationId={record.organizationId}
                scope={scope}
                onChange={(props) => dispatch({
                  type: 'UPDATE_PROPS', nodeId: selected.id, props,
                })}
              />
            )
            : <p className="adm-muted">Select a block to edit its settings.</p>}
        </aside>
      </div>

      {showPublish && (
        <PublishDialog
          owner={owner}
          name={name}
          busy={saving}
          onPublish={publish}
          onClose={() => setShowPublish(false)}
        />
      )}

      {showHistory && (
        <VersionHistory
          owner={owner}
          currentDraftVersion={state.savedVersion}
          onRestore={restore}
          onClose={() => setShowHistory(false)}
        />
      )}

      <p className="cms-footnote">
        <Link to={LIST_PATH[kind]}>&larr; Back to {LIST_LABEL[kind].toLowerCase()}</Link>
      </p>
    </div>
  );
}

/** A template or a page carries a theme assignment; a global section inherits one. */
function ownerThemeId(record: CmsTemplate | CmsPage | GlobalSection): string | null {
  return 'themeId' in record ? ((record as CmsTemplate).themeId ?? null) : null;
}

const LIST_PATH: Record<string, string> = {
  template: '/admin/content/templates',
  page: '/admin/content/pages',
  global_section: '/admin/content/global-sections',
};
const LIST_LABEL: Record<string, string> = {
  template: 'Templates', page: 'Pages', global_section: 'Global sections',
};

/** How far a change to this section reaches, stated before anybody starts editing. */
function SectionReach({ sectionId }: { sectionId: string }) {
  const [usage, setUsage] = useState<SectionUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    globalSectionUsage(sectionId)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { /* The warning still stands without the number. */ });
    return () => { cancelled = true; };
  }, [sectionId]);

  const referenced = (usage?.templates ?? 0) + (usage?.pages ?? 0);

  return (
    <p className="alert alert--warning" role="note">
      <strong>This is a global section.</strong> Publishing it changes every page that
      references it
      {usage && referenced > 0 && (
        <> — currently <strong>{referenced}</strong> published
          {referenced === 1 ? ' template or page' : ' templates and pages'}
          {usage.events > 0 && <>, affecting <strong>{usage.events}</strong> live
            event{usage.events === 1 ? '' : 's'}</>}
        </>
      )}
      {usage && referenced === 0 && <> — nothing references it yet</>}
      .
    </p>
  );
}

/** A page with nothing in it yet. Saving is what makes it a draft. */
const EMPTY_DOCUMENT: BlockDocument = {
  schemaVersion: 1,
  root: { id: 'root', component: 'Section', v: 1, props: { variant: 'plain' }, children: [] },
};

/**
 * A 409 means somebody else saved while this editor was open.
 *
 * Said plainly, because the alternative an editor usually offers — overwrite — is how one
 * person's afternoon disappears without either of them noticing.
 */
function conflictMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  return 'Somebody else has saved this draft since you opened it. Reload to see their '
    + 'version. Your changes are still on screen, so copy anything you need first.';
}
